import type {
  Project,
  ProjectPromotionStatus,
  ProjectStatus,
  ProjectType,
  SquareStatus,
} from "~/types/project";
import { imageUrl } from "~/utils/app";

const DEFAULT_PROJECT_LOGO = "/static/default-project-logo.svg";

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  company: "公司项目",
  personal: "个人项目",
  opensource: "开源项目",
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "筹备中",
  demo: "已有demo",
  running: "运营中",
  maintaining: "持续维护中",
  unmaintained: "不再维护",
  ended: "已结束",
};

export const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string }[] = [
  { value: "company", label: PROJECT_TYPE_LABELS.company },
  { value: "personal", label: PROJECT_TYPE_LABELS.personal },
  { value: "opensource", label: PROJECT_TYPE_LABELS.opensource },
];

export const PROJECT_STATUS_OPTIONS: {
  value: ProjectStatus;
  label: string;
}[] = [
  { value: "draft", label: PROJECT_STATUS_LABELS.draft },
  { value: "demo", label: PROJECT_STATUS_LABELS.demo },
  { value: "running", label: PROJECT_STATUS_LABELS.running },
  { value: "maintaining", label: PROJECT_STATUS_LABELS.maintaining },
  { value: "unmaintained", label: PROJECT_STATUS_LABELS.unmaintained },
  { value: "ended", label: PROJECT_STATUS_LABELS.ended },
];

export const getProjectTypeLabel = (type: Project["project_type"]) =>
  PROJECT_TYPE_LABELS[type] || type;

export const getProjectStatusLabel = (status: Project["status"]) =>
  PROJECT_STATUS_LABELS[status] || status;

export const SQUARE_STATUS_LABELS: Record<SquareStatus, string> = {
  published: "已展示",
  unpublished: "未展示",
  taken_down: "已下架",
};

export const SQUARE_STATUS_OPTIONS: {
  value: SquareStatus;
  label: string;
}[] = [
  { value: "published", label: SQUARE_STATUS_LABELS.published },
  { value: "unpublished", label: SQUARE_STATUS_LABELS.unpublished },
  { value: "taken_down", label: SQUARE_STATUS_LABELS.taken_down },
];

export const SQUARE_STATUS_COLORS: Record<SquareStatus, string> = {
  published: "green",
  unpublished: "default",
  taken_down: "red",
};

export const getSquareStatusLabel = (status: SquareStatus) =>
  SQUARE_STATUS_LABELS[status] || status;

/** 根据发布字段计算广场展示状态：已展示 / 未展示（从未发布）/ 已下架（发布过又被下架） */
export const resolveSquareStatus = (
  record: Pick<Project, "published" | "published_at">
): SquareStatus =>
  record.published
    ? "published"
    : record.published_at
      ? "taken_down"
      : "unpublished";

export const PROJECT_PROMOTION_STATUS_LABELS: Record<
  ProjectPromotionStatus,
  string
> = {
  pending_review: "待审核",
  queued: "排队中",
  active: "推广中",
  finished: "已结束",
  cancelled: "已取消",
  rejected: "已驳回",
};

/** 管理端筛选用：全部 + 六个状态 */
export const PROJECT_PROMOTION_STATUS_OPTIONS: {
  value: string;
  label: string;
}[] = [
  { value: "all", label: "全部" },
  ...(
    Object.keys(PROJECT_PROMOTION_STATUS_LABELS) as ProjectPromotionStatus[]
  ).map((value) => ({ value, label: PROJECT_PROMOTION_STATUS_LABELS[value] })),
];

/** antd Tag 颜色：待办高亮、进行中蓝、生效中绿、终态灰、驳回红 */
export const PROJECT_PROMOTION_STATUS_COLORS: Record<
  ProjectPromotionStatus,
  string
> = {
  pending_review: "gold",
  queued: "blue",
  active: "green",
  finished: "default",
  cancelled: "default",
  rejected: "red",
};

export const getProjectPromotionStatusLabel = (status: ProjectPromotionStatus) =>
  PROJECT_PROMOTION_STATUS_LABELS[status] || status;

/** 推广档位文案：1 个月按 30 天算 */
export const formatPromotionDuration = (days: number) =>
  days === 1 ? "1 天" : days === 7 ? "1 周" : days === 30 ? "1 个月" : `${days} 天`;

/** 「9 月 17 日 18:00」：推广横幅与推广期拦截弹窗上的到期时间 */
export const formatPromotionEndAt = (date?: string | null) => {
  if (!date) return "";
  const d = new Date(date);
  const hh = `${d.getHours()}`.padStart(2, "0");
  const mm = `${d.getMinutes()}`.padStart(2, "0");
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${hh}:${mm}`;
};

// logo_url 指向后端默认占位图（/projects/defaults/）时视为未上传
export const hasCustomProjectLogo = (logoUrl?: string | null): boolean => {
  if (!logoUrl) return false;
  return !logoUrl.includes("/projects/defaults/");
};

export const projectLogoUrl = (
  project: Pick<Project, "logo_url"> | null | undefined,
  version: number | string
) => {
  const url = project?.logo_url || DEFAULT_PROJECT_LOGO;
  if (/aliyuncs\.com/.test(url)) {
    return imageUrl(url, version);
  }
  return url;
};
